import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, effectiveTier } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { Loader2, Sparkles, BookOpen, Download, RotateCcw, FileText } from "lucide-react";

const THEMES = [
  { id: "fashion",   label: "Fashion" },
  { id: "tech",      label: "Tech" },
  { id: "lifestyle", label: "Lifestyle" },
  { id: "travel",    label: "Travel" },
  { id: "business",  label: "Business" },
];

export default function MagazineGeneratePage() {
  const { user, loading, refresh } = useAuth();
  const tier = effectiveTier(user);

  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState("gq");
  const [theme, setTheme] = useState("business");
  const [topic, setTopic] = useState("");
  const [sectionCount, setSectionCount] = useState(5);

  const [planning, setPlanning] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/magazine/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;

  const onPlan = async () => {
    setError(""); setPlan(null); setProjectId(null); setResult(null);
    if (topic.trim().length < 8) {
      setError("Tell us your magazine topic — at least 8 characters.");
      return;
    }
    setPlanning(true);
    try {
      const { data } = await api.post("/magazine/plan", {
        topic, theme, template, section_count: sectionCount,
      });
      setPlan(data.plan);
      setProjectId(data.project_id);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPlanning(false);
    }
  };

  const onRender = async (skipImages = false) => {
    setError(""); setResult(null);
    if (!projectId) return;
    setRendering(true);
    try {
      const { data } = await api.post("/magazine/render", { project_id: projectId, skip_images: skipImages });
      setResult(data);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRendering(false);
    }
  };

  const downloadHref = result ? `${BACKEND_URL}${result.download_url}` : null;

  return (
    <DashboardShell>
      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-10" data-testid="magazine-page">
        <div className="mb-7">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">
            <Sparkles className="w-3.5 h-3.5 gold-twinkle" /> Phase 11f · Magazine Generator
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold gold-shimmer">Editorial magazines, on demand</h1>
          <p className="text-[#A0A0A0] text-sm mt-2 max-w-2xl">
            Generate a full editorial layout — cover, table of contents, 4–8 illustrated sections, exported as a multi-page PDF. Lookbooks · pitch decks · visual essays · brand books.
            {tier === "premium" && <span className="text-[#D4AF37] ml-2">· Premium: unwatermarked PDFs.</span>}
          </p>
        </div>

        {!plan && (
          <div className="space-y-5 border border-[#1F1F1F] bg-[#0A0A0A] p-5 sm:p-6" data-testid="magazine-brief-form">
            <Field label="Cover template">
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                {templates.map((t) => (
                  <button key={t.id} type="button" onClick={() => setTemplate(t.id)} data-testid={`mag-template-${t.id}`}
                    className={`text-left border p-3 transition-colors ${template === t.id ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] hover:border-[#D4AF37]/60"}`}>
                    <p className={`text-xs font-bold ${template === t.id ? "text-[#D4AF37]" : "text-[#F4F4F0]"}`}>{t.label.split(" · ")[0]}</p>
                    <p className="text-[10px] text-[#A0A0A0] mt-1 leading-relaxed">{t.label.split(" · ")[1]}</p>
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Theme">
                <select data-testid="mag-theme" value={theme} onChange={(e) => setTheme(e.target.value)}
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]">
                  {THEMES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Sections (4–8)">
                <input type="number" min={4} max={8} data-testid="mag-sections" value={sectionCount} onChange={(e) => setSectionCount(Math.min(8, Math.max(4, parseInt(e.target.value) || 4)))}
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]" />
              </Field>
            </div>

            <Field label="Topic / brief" required>
              <textarea rows={3} data-testid="mag-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. The new wave of Black-owned luxury hospitality brands shaping post-pandemic travel."
                className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0] resize-none" />
            </Field>

            {error && <div className="px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="magazine-error">{error}</div>}

            <button onClick={onPlan} disabled={planning} data-testid="magazine-plan-btn"
              className="w-full inline-flex items-center justify-center gap-2 gold-sheen font-bold py-3.5 text-sm uppercase tracking-wider transition-all disabled:opacity-50 shadow-[0_8px_32px_-8px_rgba(212,175,55,0.55)]">
              {planning ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting magazine…</> : <><BookOpen className="w-4 h-4" /> Plan my magazine</>}
            </button>
            <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider text-center">Plan stage debits 3 credits · Render stage debits per-image (Pro=3cr × N)</p>
          </div>
        )}

        {plan && !result && (
          <div className="space-y-5" data-testid="magazine-plan">
            <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#0A0A0A] to-[#121212] p-5 sm:p-6">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-2">{plan.template?.toUpperCase()} · {plan.theme?.toUpperCase()}</p>
              <h2 className="font-display text-2xl font-extrabold text-[#F4F4F0]">{plan.title} · {plan.issue}</h2>
              <p className="text-sm text-[#A0A0A0] mt-2">{plan.cover_headline}</p>
              <p className="text-xs text-[#5A5A5A] mt-1 italic">{plan.cover_subhead}</p>
            </div>

            {(plan.sections || []).map((s, idx) => (
              <div key={idx} data-testid={`mag-section-${idx}`} className="border border-[#2A2A2A] bg-[#0A0A0A] p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 bg-[#D4AF37] text-[#080808] font-bold text-xs">{idx + 1}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-semibold">{s.kicker}</span>
                </div>
                <h3 className="font-display text-xl font-bold text-[#F4F4F0] mb-2">{s.title}</h3>
                <p className="text-sm text-[#C8C8C8] leading-relaxed line-clamp-4">{s.body}</p>
                <p className="text-xs italic text-[#D4AF37] mt-3">"{s.pull_quote}"</p>
              </div>
            ))}

            {error && <div className="px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm">{error}</div>}

            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => { setPlan(null); setProjectId(null); }} data-testid="magazine-restart"
                className="inline-flex items-center justify-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-5 py-3 text-sm transition-colors">
                <RotateCcw className="w-3.5 h-3.5" /> Start over
              </button>
              <button onClick={() => onRender(true)} disabled={rendering} data-testid="magazine-render-text"
                className="inline-flex items-center justify-center gap-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] px-5 py-3 text-sm transition-colors">
                <FileText className="w-3.5 h-3.5" /> Text-only PDF (free)
              </button>
              <button onClick={() => onRender(false)} disabled={rendering} data-testid="magazine-render-full"
                className="flex-1 inline-flex items-center justify-center gap-2 gold-sheen font-bold py-3.5 text-sm uppercase tracking-wider transition-all disabled:opacity-50">
                {rendering ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering illustrations…</> : <><BookOpen className="w-4 h-4" /> Render full illustrated PDF</>}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-5" data-testid="magazine-result">
            <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#0A0A0A] to-[#121212] p-8 text-center">
              <BookOpen className="w-12 h-12 text-[#D4AF37] mx-auto mb-4 gold-twinkle" />
              <h2 className="font-display text-3xl font-extrabold gold-shimmer mb-2">Your magazine is ready</h2>
              <p className="text-sm text-[#A0A0A0]">{result.pages} pages · multi-page PDF</p>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              <button onClick={() => { setPlan(null); setProjectId(null); setResult(null); }} className="border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-4 py-2.5 text-sm transition-colors inline-flex items-center gap-2">
                <RotateCcw className="w-3.5 h-3.5" /> New magazine
              </button>
              <a href={downloadHref} data-testid="magazine-download" className="gold-sheen text-[#080808] font-bold px-6 py-3 text-sm uppercase tracking-wider inline-flex items-center gap-2 transition-all">
                <Download className="w-3.5 h-3.5" /> Download PDF
              </a>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </span>
      {children}
    </label>
  );
}
